import {CurrencyPipe, DatePipe, CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, Inject} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Integration} from '@gatewee/types/integration';
import {Pool} from '@gatewee/types/pool';
import {
  AssociateWalletPayload,
  Wallet,
  walletBindingScope,
  walletProvider,
} from '@gatewee/types/wallet';
import {UtilsModule} from '@gatewee/utils';
import {
  TuiAlertService,
  TuiDialogContext,
  TuiDialogService,
} from '@taiga-ui/core';
import {TuiStatus, TuiTabs} from '@taiga-ui/kit';
import {
  POLYMORPHEUS_CONTEXT,
  PolymorpheusComponent,
} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {
  IntegrationRoutingRuleActions,
  IntegrationRoutingRulesComponent,
} from '../integration-routing-rules/integration-routing-rules.component';
import {WalletAssociateFormComponent} from '../associate/wallet-associate-form.component';

export interface WalletViewDialogData {
  wallet: Wallet;
  syncWalletFn: (walletId: number) => Observable<Wallet>;
  setPrimaryFn: (walletId: number) => Observable<Wallet>;
  routingRuleActions?: IntegrationRoutingRuleActions;
  associateIntegrationFn?: (
    walletId: number,
    payload: AssociateWalletPayload,
  ) => Observable<Wallet>;
  fetchIntegrationsFn?: (
    entityId: number,
    inUse?: boolean,
  ) => Observable<Integration[]>;
  fetchPoolsFn?: () => Observable<Pool[]>;
  recipientId?: number;
}

@Component({
  selector: 'app-wallet-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    UtilsModule,
    GateweeButtonComponent,
    IntegrationRoutingRulesComponent,
    TuiStatus,
    ...TuiTabs,
    CurrencyPipe,
    DatePipe,
  ],
  templateUrl: './wallet-view.component.html',
  styleUrls: ['./wallet-view.component.css'],
  providers: [CurrencyPipe, DatePipe],
})
export class WalletViewComponent {
  readonly walletProvider = walletProvider;
  readonly walletBindingScope = walletBindingScope;

  activeTabIndex = 0;
  syncing = false;
  settingPrimary = false;

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, WalletViewDialogData>,
    private readonly dialogService: TuiDialogService,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
  ) {}

  get data(): WalletViewDialogData {
    return this.context.data;
  }

  get wallet(): Wallet {
    return this.data.wallet;
  }

  get routingRuleActions(): IntegrationRoutingRuleActions | undefined {
    return this.data.routingRuleActions;
  }

  get canAssociateIntegration(): boolean {
    return (
      walletBindingScope(this.wallet) === null &&
      !!this.data.associateIntegrationFn &&
      !!this.data.fetchIntegrationsFn &&
      !!this.data.fetchPoolsFn &&
      this.data.recipientId !== undefined
    );
  }

  openAssociateDialog(): void {
    const {
      associateIntegrationFn,
      fetchIntegrationsFn,
      fetchPoolsFn,
      recipientId,
    } = this.data;

    if (
      !associateIntegrationFn ||
      !fetchIntegrationsFn ||
      !fetchPoolsFn ||
      recipientId === undefined
    ) {
      return;
    }

    this.dialogService
      .open<Wallet | false>(
        new PolymorpheusComponent(WalletAssociateFormComponent),
        {
          data: {
            walletId: this.wallet.id,
            recipientId,
            fetchIntegrationsFn,
            fetchPoolsFn,
            associateFn: associateIntegrationFn,
          },
          size: 'm',
          label: this.translate.instant('features.wallets.associate.title', {
            id: this.wallet.id,
          }),
          dismissible: true,
          closeable: true,
        },
      )
      .subscribe(updated => {
        if (updated) {
          this.data.wallet = updated;
          this.context.completeWith(true);
        }
      });
  }

  syncBalance(): void {
    if (this.syncing) {
      return;
    }

    this.syncing = true;
    this.data.syncWalletFn(this.wallet.id).subscribe({
      next: updated => {
        this.syncing = false;
        this.data.wallet = updated;
        this.alerts
          .open(this.translate.instant('features.wallets.syncSuccess'), {
            label: this.translate.instant('common.status.success'),
            appearance: 'success',
          })
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: HttpErrorResponse) => {
        this.syncing = false;
        this.alerts
          .open(
            error.error?.message ??
              this.translate.instant('features.wallets.syncError'),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }

  setPrimary(): void {
    if (this.settingPrimary || this.wallet.isPrimary) {
      return;
    }

    this.settingPrimary = true;
    this.data.setPrimaryFn(this.wallet.id).subscribe({
      next: updated => {
        this.settingPrimary = false;
        this.data.wallet = {...this.wallet, ...updated, isPrimary: true};
        this.alerts
          .open(this.translate.instant('features.wallets.setPrimarySuccess'), {
            label: this.translate.instant('common.status.success'),
            appearance: 'success',
          })
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: HttpErrorResponse) => {
        this.settingPrimary = false;
        this.alerts
          .open(
            error.error?.message ??
              this.translate.instant('features.wallets.setPrimaryError'),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }
}
